import React, { useMemo, useRef } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../configs';
import { useAuth } from './auth';

const axiosContext = React.createContext();

/** Same rules as axios `combineURLs` + `buildFullPath` for merging instance baseURL with request url. */
function mergeAxiosBaseUrlAndPath(baseURL, url) {
    if (url == null || url === '') {
        return baseURL || '';
    }
    const u = String(url);
    if (/^https?:\/\//i.test(u)) {
        return u;
    }
    const base = (baseURL || '').replace(/\/+$/, '');
    const rel = u.replace(/^\/+/, '');
    if (!base) {
        return rel.startsWith('/') ? rel : `/${rel}`;
    }
    return `${base}/${rel}`;
}

/**
 * Infra API on Cloud Run may 307 to `http://.../path?query`, which HTTPS pages cannot follow (mixed content).
 * - Collapse redundant trailing slashes on most paths.
 * - Keep canonical trailing slash for `/infra/unitcosting` (Flask strict_slashes redirects bare path to
 *   `http://.../unitcosting/?query`, which breaks HTTPS clients).
 * - Force https for our *.a.run.app hosts when the URL was built with http.
 */
function normalizeInfraAbsoluteUrl(absoluteUrl) {
    if (!absoluteUrl || !/^https?:\/\//i.test(absoluteUrl)) {
        return absoluteUrl;
    }
    try {
        const parsed = new URL(absoluteUrl);
        if (parsed.pathname.length > 1) {
            const stripped = parsed.pathname.replace(/\/+$/, '');
            if (stripped === '/infra/unitcosting') {
                parsed.pathname = '/infra/unitcosting/';
            } else {
                parsed.pathname = stripped;
            }
        }
        if (
            parsed.protocol === 'http:' &&
            parsed.hostname.endsWith('.a.run.app')
        ) {
            parsed.protocol = 'https:';
        }
        return parsed.href;
    } catch {
        return absoluteUrl;
    }
}

export const AxiosProvider = ({ children }) => {
    const { getAccessToken, invalidateUser } = useAuth();
    const authRef = useRef({ getAccessToken, invalidateUser });
    authRef.current = { getAccessToken, invalidateUser };

    /** One client for the app lifetime; avoids new identity every render (which retriggered profile fetch effects). */
    const authAxios = useMemo(() => {
        const instance = axios.create({
            baseURL: BACKEND_URL,
        });

        instance.interceptors.request.use(
            (config) => {
                if (config.url != null && config.url !== '') {
                    const merged = mergeAxiosBaseUrlAndPath(
                        config.baseURL,
                        config.url
                    );
                    const next = normalizeInfraAbsoluteUrl(merged);
                    if (/^https?:\/\//i.test(next)) {
                        config.baseURL = '';
                        config.url = next;
                    }
                }
                config.headers.Authorization =
                    'Bearer ' + authRef.current.getAccessToken();
                return config;
            },
            (error) => Promise.reject(error),
            { synchronous: true }
        );

        instance.interceptors.response.use(
            (response) => response,
            (error) => {
                if (!!error.response && error.response.status === 401) {
                    authRef.current.invalidateUser();
                }
                return Promise.reject(error);
            }
        );

        return instance;
    }, []);

    return (
        <axiosContext.Provider value={authAxios}>
            {children}
        </axiosContext.Provider>
    );
};

export const useAxios = () => {
    return React.useContext(axiosContext);
};
